import React, { useEffect, useMemo, useState } from "react";
import Modal from "components/ui/Modal";
import PageLoader from "components/ui/PageLoader";
import Table from "components/ui/Table";
import GroupChatPanel from "components/commsLog/GroupChatPanel";
import ManualTrigger from "components/commsLog/ManualTrigger";
import VendorGroupMessage from "components/commsLog/VendorGroupMessage";
import MessageDetail from "components/commsLog/MessageDetail";
import StateBadge from "components/commsLog/StateBadge";
import {
  useCommsMessagesQuery,
  useCommsOrganizationsQuery,
  useCommsPropertiesQuery,
} from "apis/commsLog";
import {
  formatSydney,
  skipReasons,
  stateOptions,
  windowOptions,
} from "utils/commsLogStates";

const TABS = [
  { key: "messages", label: "Messages" },
  { key: "properties", label: "Properties & group chats" },
];

const CommsPropVendor = () => {
  const [tab, setTab] = useState("messages");
  const [organizationId, setOrganizationId] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");
  const [propertyFilter, setPropertyFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [openMessage, setOpenMessage] = useState(null);
  const [openProperty, setOpenProperty] = useState(null);
  const [triggerProperty, setTriggerProperty] = useState(null);
  const [writeToProperty, setWriteToProperty] = useState(null);

  const { data: organizations, isPending: organizationsPending } =
    useCommsOrganizationsQuery();

  // The list arrives sorted by volume, so the screen opens on the busiest office.
  useEffect(() => {
    if (!organizationId && organizations.length) {
      setOrganizationId(organizations[0]._id);
    }
  }, [organizations, organizationId]);

  const messageFilters = useMemo(
    () => ({
      organizationId,
      state: selectedState || undefined,
      days,
      search: search || undefined,
      propertyId: propertyFilter?.propertyId || undefined,
      page,
      limit,
    }),
    [organizationId, selectedState, days, search, propertyFilter, page, limit],
  );

  const propertyFilters = useMemo(
    () => ({ organizationId, days, search: search || undefined, page, limit }),
    [organizationId, days, search, page, limit],
  );

  const messages = useCommsMessagesQuery(messageFilters);
  const properties = useCommsPropertiesQuery(propertyFilters);

  const resetPage = (apply) => {
    setPage(1);
    apply();
  };

  const messageColumns = [
    {
      header: "Property",
      accessorKey: "address",
      minWidth: "220px",
      wrap: true,
      cell: (row) => (
        <div>
          <p className="text-sm">{row.address || "Unknown property"}</p>
          <p className="text-xs text-darkgray">{row.organizationName || "—"}</p>
        </div>
      ),
    },
    {
      header: "Template",
      accessorKey: "templateLabel",
      minWidth: "180px",
      wrap: true,
      cell: (row) => (
        <span className="text-sm">{row.templateLabel || row.templateName || "—"}</span>
      ),
    },
    {
      header: "Status",
      accessorKey: "state",
      minWidth: "190px",
      wrap: true,
      cell: (row) => (
        <div>
          <StateBadge state={row.state} />
          {/* The reason is what dev ops are scanning for, so it sits in the row. */}
          {row.delivery?.state === "failed" ? (
            <p className="mt-1 text-xs text-red-600">{row.delivery.error}</p>
          ) : row.skipReason ? (
            <p className="mt-1 text-xs text-darkgray">
              {skipReasons[row.skipReason] || row.skipReason}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      header: "When",
      accessorKey: "occurredAt",
      cell: (row) => (
        <span className="whitespace-nowrap text-sm">
          {formatSydney(row.occurredAt)}
        </span>
      ),
    },
    { header: "Agent", accessorKey: "agentName" },
  ];

  const propertyColumns = [
    {
      header: "Property",
      accessorKey: "address",
      minWidth: "240px",
      wrap: true,
      cell: (row) => (
        <div>
          <p className="text-sm">{row.address || "Unknown property"}</p>
          <p className="text-xs text-darkgray">
            {row.organizationName || "—"} · {row.agentName || "—"}
          </p>
        </div>
      ),
    },
    {
      header: "Group chat",
      accessorKey: "hasGroupChat",
      cell: (row) =>
        row.hasGroupChat ? (
          <span
            className={`rounded-full px-2.5 py-1 text-xs ${
              row.groupChatArchived
                ? "bg-orange-100 text-orange-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {row.groupChatArchived ? "Archived" : "Open"}
          </span>
        ) : (
          <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs text-red-700">
            None
          </span>
        ),
    },
    { header: "Messages", accessorKey: "total" },
    { header: "Sent", accessorKey: "sent" },
    {
      header: "Failed",
      accessorKey: "failed",
      cell: (row) => (
        <span className={row.failed ? "font-semibold text-red-600" : ""}>
          {row.failed}
        </span>
      ),
    },
    { header: "Scheduled", accessorKey: "scheduled" },
    {
      header: "Last activity",
      accessorKey: "lastActivityAt",
      cell: (row) => (
        <span className="whitespace-nowrap text-sm">
          {formatSydney(row.lastActivityAt)}
        </span>
      ),
    },
  ];

  if (organizationsPending) return <PageLoader />;

  const active = tab === "messages" ? messages : properties;

  return (
    <div className="container">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h4>Vendor comms log</h4>
        {/* The 2am case, reachable without knowing to click into a property first. */}
        <div className="flex flex-wrap gap-2">
          {/* Nothing in the campaign covers it, so the admin writes it themselves. */}
          <button
            type="button"
            className="rounded-md border border-black px-4 py-2 text-sm"
            onClick={() => setWriteToProperty({})}
          >
            Write to a vendor group
          </button>
          <button
            type="button"
            className="rounded-md bg-black px-4 py-2 text-sm text-white"
            onClick={() => setTriggerProperty({})}
          >
            Trigger a message
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-darkgray">Organisation</span>
          <select
            className="min-w-[220px] rounded-md border border-mediumgray px-3 py-2 text-sm"
            value={organizationId}
            onChange={(event) =>
              resetPage(() => setOrganizationId(event.target.value))
            }
          >
            <option value="all">All organisations</option>
            {organizations.map((organization) => (
              <option key={organization._id} value={organization._id}>
                {organization.name} ({organization.messages})
              </option>
            ))}
          </select>
        </label>

        {tab === "messages" ? (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-darkgray">Status</span>
            <select
              className="min-w-[180px] rounded-md border border-mediumgray px-3 py-2 text-sm"
              value={selectedState}
              onChange={(event) =>
                resetPage(() => setSelectedState(event.target.value))
              }
            >
              {stateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="flex flex-col gap-1">
          <span className="text-xs text-darkgray">Period</span>
          <select
            className="min-w-[160px] rounded-md border border-mediumgray px-3 py-2 text-sm"
            value={days}
            onChange={(event) =>
              resetPage(() => setDays(Number(event.target.value)))
            }
          >
            {windowOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {propertyFilter && tab === "messages" ? (
          <button
            type="button"
            className="rounded-md border border-mediumgray px-3 py-2 text-sm"
            onClick={() => resetPage(() => setPropertyFilter(null))}
          >
            Showing {propertyFilter.address} · clear
          </button>
        ) : null}
      </div>

      <div className="mb-4 flex gap-2 border-b border-mediumgray">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`px-4 py-2 text-sm ${
              tab === item.key
                ? "border-b-2 border-black font-semibold"
                : "text-darkgray"
            }`}
            onClick={() => {
              setPage(1);
              setTab(item.key);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {active.isError ? (
        <p className="text-sm text-red-600">
          Could not load the comms log. Try again.
        </p>
      ) : null}

      {tab === "messages" ? (
        <Table
          columns={messageColumns}
          data={messages.data}
          emptyText={
            messages.isPending || messages.isFetching
              ? "Loading…"
              : "No messages for these filters."
          }
          searchPlaceholder="Search address or message text…"
          defaultSearch={search}
          onSearchChange={(query) => resetPage(() => setSearch(query))}
          serverPagination={messages.pagination}
          onPageChange={setPage}
          pageSizeOptions={[25, 50, 100]}
          onPageSizeChange={(size) => resetPage(() => setLimit(size))}
          onRowClick={(row) => setOpenMessage(row)}
        />
      ) : (
        <Table
          columns={propertyColumns}
          data={properties.data}
          emptyText={
            properties.isPending || properties.isFetching
              ? "Loading…"
              : "No properties for these filters."
          }
          searchPlaceholder="Search address…"
          defaultSearch={search}
          onSearchChange={(query) => resetPage(() => setSearch(query))}
          serverPagination={properties.pagination}
          onPageChange={setPage}
          pageSizeOptions={[25, 50, 100]}
          onPageSizeChange={(size) => resetPage(() => setLimit(size))}
          onRowClick={(row) => setOpenProperty(row)}
        />
      )}

      {openMessage ? (
        <MessageDetail row={openMessage} onClose={() => setOpenMessage(null)} />
      ) : null}

      {openProperty ? (
        <Modal
          isOpen
          onClose={() => setOpenProperty(null)}
          title={openProperty.address || "Property"}
          className="max-w-2xl"
        >
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-md bg-black px-4 py-2 text-sm text-white"
              onClick={() => {
                setPropertyFilter(openProperty);
                setOpenProperty(null);
                setPage(1);
                setTab("messages");
              }}
            >
              See its {openProperty.total} messages
            </button>
            <button
              type="button"
              className="rounded-md border border-black px-4 py-2 text-sm"
              onClick={() => {
                setTriggerProperty(openProperty);
                setOpenProperty(null);
              }}
            >
              Trigger a message
            </button>
          </div>
          <GroupChatPanel propertyId={openProperty.propertyId} />
        </Modal>
      ) : null}

      {writeToProperty ? (
        <VendorGroupMessage
          property={writeToProperty.propertyId ? writeToProperty : null}
          organizationId={organizationId}
          onClose={() => setWriteToProperty(null)}
        />
      ) : null}

      {triggerProperty ? (
        <ManualTrigger
          property={triggerProperty.propertyId ? triggerProperty : null}
          organizationId={organizationId}
          onClose={() => setTriggerProperty(null)}
        />
      ) : null}
    </div>
  );
};

export default CommsPropVendor;
